import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import NewsletterForm from '../ui/NewsletterForm';
import { useServiceFinder } from '../ui/ServiceFinderContext';
import { usePortalEnabled } from '../../hooks/usePortalEnabled';

const serviceLinks = [
  { label: 'Design Audit', path: '/services/website-design-audit' },
  { label: 'SEO Reality Check', path: '/services/seo-reality-check' },
  { label: 'Content Writing & SEO', path: '/services/website-content-writing' },
  { label: 'Homepage Prototypes', path: '/services/two-strategic-homepage-prototypes' },
  { label: 'Custom Website Build', path: '/services/fully-custom-website-build' },
  { label: 'Complete Website Build', path: '/services/complete-website-build' },
  { label: 'Architecture Build', path: '/services/strategic-architecture-build' },
];

const resourceLinks = [
  { label: 'Launch Checklist', path: '/resources/website-launch-checklist' },
  { label: 'Ownership Checklist', path: '/resources/website-ownership-checklist' },
  { label: 'Budget Calculator', path: '/resources/website-budget-calculator' },
  { label: 'All Resources', path: '/resources' },
];

const baseCompanyLinks = [
  { label: 'About', path: '/about' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
  { label: 'Terms of Service', path: '/terms' },
];

export default function Footer() {
  const { openFinder } = useServiceFinder();
  const location = useLocation();
  const portalEnabled = usePortalEnabled();

  const companyLinks = portalEnabled
    ? [
        ...baseCompanyLinks.slice(0, 4),
        { label: 'Client Portal', path: '/client' },
      ]
    : baseCompanyLinks;

  return (
    <footer className="bg-night-950 text-white">
      <div className="container-wide pt-20 sm:pt-28 pb-12">
        <div className="mb-20">
          <p className="text-display-md font-black uppercase leading-none tracking-tighter">
            Let's build
            <br />
            <span className="text-volt-500">something</span> real.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button onClick={openFinder} className="btn-volt">
              Start a project
              <ArrowUpRight size={16} />
            </button>
            <Link
              to="/services"
              className="btn-ghost"
              onClick={(e) => {
                if (location.pathname === '/services') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              Explore services
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-night-400 mb-5">Services</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-night-300 hover:text-volt-500 transition-colors inline-block py-1"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-night-400 mb-5">Resources</h4>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-night-300 hover:text-volt-500 transition-colors inline-block py-1"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-night-400 mb-5">Company</h4>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-night-300 hover:text-volt-500 transition-colors inline-block py-1"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-night-400 mb-5">Newsletter</h4>
            <p className="text-sm text-night-400 mb-4">
              Practical website tips. No spam.
            </p>
            <NewsletterForm variant="dark" />
          </div>
        </div>

        <div className="pt-8 border-t border-night-800 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm font-black uppercase tracking-tighter hover:opacity-80 transition-opacity">
              W<span className="text-volt-500">A</span>S
            </Link>
            <span className="text-night-700">|</span>
            <p className="-ml-0.5 text-xs text-night-500">
              &copy; {new Date().getFullYear()} <span className="font-bold text-night-400">Website Audit Studio</span>. All rights reserved.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-x-3 text-2xs font-bold uppercase tracking-widest text-night-500">
            <span>No hidden fees</span>
            <span className="hidden sm:inline-block w-1 h-1 bg-volt-500 rounded-full flex-shrink-0" />
            <span>Live &amp; Configured</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
